import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";

function DiarySetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const teacherId = localStorage.getItem("userId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      subject: "",
      description: "",
      classId: "",
      classSectionId: "",
      sectionSubjectId: "",
      classTeacherId: localStorage.getItem("userId"),
      createdBy: localStorage.getItem("userId"),
      attachments: [],
      dairyDate: new Date().toISOString().split('T')[0],
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [diariesData, setDiariesData] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("form");

  // Watch form values
  const subjectValue = watch("subject");
  const descriptionValue = watch("description");
  const centerIdValue = watch("centerId");

  // Fetch diaries
  useEffect(() => {
    const fetchDiaries = async () => {
      try {
        const response = await get(
          `MobileApp/GetMobileAppDailyDiary?id=${teacherId}&isTeacher=${true}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setDiariesData(response?.dailyDiary || []);
      } catch (error) {
        console.error("Error fetching diaries:", error);
      }
    };
    fetchDiaries();
  }, [triggerRerender]);

  // Fetch centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response || []);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
            fetchClassesByCenterId(savedCenterId);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    fetchCenters();
  }, []);

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenter?id=${teacherId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassesData(response || []);
        return response || [];
      } else {
        setClassesData([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      return [];
    }
  };

  const fetchClassSectionsByCenterClassId = async (centerClassId) => {
    try {
      if (centerClassId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenter?centerClassId=${centerClassId}&centerId=${savedCenterId}&userId=${teacherId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassSectionsData(response || []);
        return response || [];
      } else {
        setClassSectionsData([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching class sections:", error);
      return [];
    }
  };

  const fetchSubjectsByClassSectionId = async (classSectionId) => {
    try {
      const response = await get(
        `StudentEvaluation/GetSubject?ClassSectionId=${classSectionId}&userId=${teacherId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSubjectsData(response?.subjects || []);
      return response?.subjects || [];
    } catch (error) {
      console.error("Error fetching subjects:", error);
      return [];
    }
  };

  const handleEdit = async (id, rowAttachmentPath) => {
    try {
      const response = await get(`MobileApp/DailyDairyById?Id=${id}&centerId=${centerId}&attachmentPath=${rowAttachmentPath || ""}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response && (response.isSuccess || response.id)) {
        const diary = response.dailyDiary || response; // Handle both wrapped and flat response
        
        setIsEditing(true);
        setEditId(id);
        setActiveTab("form");

        // Set simple values
        setValue("subject", diary.subject);
        setValue("description", diary.description);
        setValue("dairyDate", diary.dairyDate ? diary.dairyDate.split('T')[0] : new Date().toISOString().split('T')[0]);
        
        // Handle attachments
        if (diary.attachmentPath) {
          setValue("attachmentPath", diary.attachmentPath);
          setValue("attachmentName", diary.attachmentName);
          setSelectedFiles([{
            name: diary.attachmentName || "View Attachment",
            isExisting: true,
            path: diary.attachmentPath,
            size: 0
          }]);
        } else {
          setValue("attachmentPath", "");
          setValue("attachmentName", "");
          setSelectedFiles([]);
        }

        // Try to find missing centerId and classId from diariesData list
        let fetchedCenterId = diary.centerId;
        let fetchedClassId = diary.classId;

        if (!fetchedCenterId || !fetchedClassId) {
          const listRecord = diariesData.find(d => d.id === parseInt(id));
          if (listRecord) {
            fetchedCenterId = fetchedCenterId || listRecord.centerId;
            fetchedClassId = fetchedClassId || listRecord.classId;
          }
        }

        // Fallback to savedCenterId if still missing
        if (!fetchedCenterId && savedCenterId) {
          fetchedCenterId = parseInt(savedCenterId);
        }

        // Handle dependent dropdowns - Center
        if (fetchedCenterId) {
          const center = centersData.find(c => c.id === fetchedCenterId);
          if (center) {
            setValue("centerId", { value: center.id, label: center.name });
          }
          
          // Fetch and set Class
          const classes = await fetchClassesByCenterId(fetchedCenterId);
          if (fetchedClassId) {
            const cls = classes?.find(c => c.id === fetchedClassId);
            if (cls) {
              setValue("classId", { value: cls.id, label: cls.name });
            }

            // Fetch and set Section
            const sections = await fetchClassSectionsByCenterClassId(fetchedClassId);
            
            if (diary.classSectionId) {
              const sec = sections.find(s => s.id === diary.classSectionId);
              if (sec) {
                setValue("classSectionId", { value: sec.id, label: sec.name });
              }

              // Fetch and set Subject
              const subjects = await fetchSubjectsByClassSectionId(diary.classSectionId);
              if (diary.sectionSubjectId) {
                const sub = subjects.find(s => s.id === diary.sectionSubjectId);
                if (sub) {
                  setValue("sectionSubjectId", { value: sub.id, label: sub.name });
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching diary for edit:", error);
      toast.error("Failed to fetch diary details.");
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    
    // Convert first file to base64 for API compatibility
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Content = reader.result.split(",")[1];
        setValue("base64String", base64Content);
        setValue("attachmentName", file.name);
        setValue("attachmentPath", ""); // Clear existing path if new file is selected
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected file
  const removeFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    
    // If we removed the first file or all files, update/clear the API fields
    if (index === 0) {
      if (updatedFiles.length > 0) {
        const file = updatedFiles[0];
        if (file.isExisting) {
          setValue("base64String", null);
          setValue("attachmentName", file.name);
          setValue("attachmentPath", file.path);
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Content = reader.result.split(",")[1];
            setValue("base64String", base64Content);
            setValue("attachmentName", file.name);
            setValue("attachmentPath", "");
          };
          reader.readAsDataURL(file);
        }
      } else {
        setValue("base64String", null);
        setValue("attachmentName", null);
        setValue("attachmentPath", "");
      }
    }
  };

  // Table columns with report-style design
  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => (
        <div style={{ 
          textAlign: 'center',
          fontWeight: 500,
          color: '#111827'
        }}>
          {row.index + 1}
        </div>
      ),
    },
    {
      header: "Title",
      accessorKey: "subject",
      Cell: ({ cell }) => (
        <div style={{ 
          fontWeight: 500,
          color: '#111827'
        }}>
          {cell.getValue()}
        </div>
      ),
    },
    {
      header: "Description",
      accessorKey: "description",
      Cell: ({ cell }) => (
        <div style={{ 
          color: '#6b7280',
          fontSize: '12px'
        }}>
          {cell.getValue() || "—"}
        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "dairyDate",
      Cell: ({ cell }) => (
        <div style={{ 
          color: '#6b7280',
          fontSize: '12px'
        }}>
          {cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "—"}
        </div>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.original.id, row.original.attachmentPath);
            }}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              fontSize: "16px",
              padding: "4px",
              borderRadius: "4px",
              transition: "background-color 0.2s"
            }}
            title="Edit Diary"
            onMouseEnter={(e) => e.target.style.backgroundColor = "#eff6ff"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
          >
            <i className="fas fa-edit"></i>
          </button>
        </div>
      ),
    },
  ];

  const saveData = async (data) => {
    if (!token) {
      toast.error("Unauthorized. Please login again.");
      return;
    }

    const formdata = {
      ...data,
      id: editId || 0,
      centerId: data.centerId?.value || "",
      classId: data.classId?.value || "",
      classSectionId: data.classSectionId?.value || "",
      sectionSubjectId: data.sectionSubjectId?.value || "",
      subjectName: data.sectionSubjectId?.label || "",
      classTeacherId: teacherId || 0,
      dairyDate: data.dairyDate ? new Date(data.dairyDate).toISOString() : new Date().toISOString(),
      isTeacher: true,
      base64String: data.base64String || null,
      attachmentName: data.attachmentName || null,
      attachmentPath: data.attachmentPath || "",
      createdBy: teacherId
    };

    try {
      const endpoint = isEditing ? "MobileApp/AddDailyDairy" : "MobileApp/AddDailyDairy"; // Using same endpoint as per user request/api list
      const response = await post(endpoint, formdata, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message || `Diary ${isEditing ? 'updated' : 'saved'} successfully!`);
        setTriggerRerender((prev) => !prev);
        setSelectedFiles([]);
        setIsEditing(false);
        setEditId(null);
        reset({
          subject: "",
          description: "",
          classId: "",
          classSectionId: "",
          sectionSubjectId: "",
          attachments: [],
          base64String: null,
          attachmentName: null,
          centerId: centerIdValue,
          dairyDate: new Date().toISOString().split('T')[0],
        });
      }
    } catch (error) {
      toast.error("Failed to save diary. Please try again.");
      console.error("Error saving diary:", error);
    }
  };

  // Custom styles for select components
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "36px",
      borderColor: "#d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      "&:hover": {
        borderColor: "#9ca3af",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
      fontSize: "14px",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "14px",
    }),
  };

  return (
    <div style={{
      backgroundColor: "#ffffff",
      minHeight: "100vh",
      padding: "20px"
    }}>
      {/* Header Section - Report Style */}
      <div style={{
        marginBottom: "20px",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: "16px"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px"
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 600,
              color: "#111827"
            }}>
              Daily Diary Management
            </h1>
            <p style={{
              margin: "4px 0 0 0",
              fontSize: "14px",
              color: "#6b7280"
            }}>
              Create and manage daily diaries for students
            </p>
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            {/* <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "#f3f4f6",
              padding: "6px 12px",
              borderRadius: "6px",
              fontSize: "13px"
            }}>
              <i className="fas fa-user" style={{ color: "#6b7280" }}></i>
              <span style={{ color: "#374151" }}>Teacher ID: {teacherId}</span>
            </div> */}
          </div>
        </div>
      </div>

      {/* Tabs Navigation - Report Style */}
      <div style={{
        display: "flex",
        borderBottom: "1px solid #e5e7eb",
        marginBottom: "24px",
        backgroundColor: "#f9fafb",
        borderRadius: "6px 6px 0 0"
      }}>
        <button
          onClick={() => setActiveTab("form")}
          style={{
            padding: "12px 20px",
            backgroundColor: activeTab === "form" ? "#ffffff" : "transparent",
            border: "none",
            borderBottom: activeTab === "form" ? "2px solid #3b82f6" : "2px solid transparent",
            color: activeTab === "form" ? "#111827" : "#6b7280",
            fontWeight: activeTab === "form" ? 600 : 500,
            fontSize: "14px",
            cursor: "pointer",
            transition: "all 0.2s",
            borderRadius: "6px 0 0 0"
          }}
        >
          <i className="fas fa-plus-circle" style={{ marginRight: "8px" }}></i>
          Create New Diary
        </button>
        <button
          onClick={() => setActiveTab("records")}
          style={{
            padding: "12px 20px",
            backgroundColor: activeTab === "records" ? "#ffffff" : "transparent",
            border: "none",
            borderBottom: activeTab === "records" ? "2px solid #3b82f6" : "2px solid transparent",
            color: activeTab === "records" ? "#111827" : "#6b7280",
            fontWeight: activeTab === "records" ? 600 : 500,
            fontSize: "14px",
            cursor: "pointer",
            transition: "all 0.2s"
          }}
        >
          <i className="fas fa-list" style={{ marginRight: "8px" }}></i>
          Diary Records
          <span style={{
            marginLeft: "8px",
            backgroundColor: "#e5e7eb",
            color: "#4b5563",
            padding: "2px 8px",
            borderRadius: "10px",
            fontSize: "11px",
            fontWeight: 500
          }}>
            {diariesData.length}
          </span>
        </button>
      </div>

      {/* Content Area */}
      {activeTab === "form" ? (
        <div style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          overflow: "hidden"
        }}>
          {/* Form Header */}
          <div style={{
            padding: "16px 20px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb"
          }}>
            <h2 style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 600,
              color: "#111827",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <i className="fas fa-edit" style={{ color: "#3b82f6" }}></i>
              Diary Entry Form
            </h2>
            <p style={{
              margin: "4px 0 0 0",
              fontSize: "13px",
              color: "#6b7280"
            }}>
              Fill in all required fields to create a new diary entry
            </p>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit(saveData)} style={{ padding: "20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              {/* Left Column */}
              <div>
                {/* Center Selection */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-school" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Center <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "Center is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption);
                          if (selectedOption) {
                            setValue("classId", null);
                            setValue("classSectionId", null);
                            setValue("sectionSubjectId", null);
                            setClassesData([]);
                            setClassSectionsData([]);
                            setSubjectsData([]);
                            fetchClassesByCenterId(selectedOption.value);
                          }
                        }}
                        styles={customSelectStyles}
                        classNamePrefix="select"
                        placeholder="Select center"
                        isClearable
                      />
                    )}
                  />
                  {errors.centerId && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.centerId.message}
                    </p>
                  )}
                </div>

                {/* Class Selection */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-graduation-cap" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Class <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="classId"
                    control={control}
                    rules={{ required: "Year group is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classesData.map((classItem) => ({
                          value: classItem.id,
                          label: classItem.name,
                        }))}
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption);
                          if (selectedOption) {
                            setValue("classSectionId", null);
                            setValue("sectionSubjectId", null);
                            fetchClassSectionsByCenterClassId(selectedOption.value);
                          }
                        }}
                        styles={customSelectStyles}
                        classNamePrefix="select"
                        placeholder="Select Class"
                        isClearable
                      />
                    )}
                  />
                  {errors.classId && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.classId.message}
                    </p>
                  )}
                </div>

                {/* Section Selection */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-layer-group" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Section <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="classSectionId"
                    control={control}
                    rules={{ required: "Section is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSectionsData.map((section) => ({
                          value: section.id,
                          label: section.name,
                        }))}
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption);
                          if (selectedOption) {
                            setValue("sectionSubjectId", null);
                            fetchSubjectsByClassSectionId(selectedOption.value);
                          }
                        }}
                        styles={customSelectStyles}
                        classNamePrefix="select"
                        placeholder="Select section"
                        isClearable
                      />
                    )}
                  />
                  {errors.classSectionId && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.classSectionId.message}
                    </p>
                  )}
                </div>

                {/* Subject Selection */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-book" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Subject <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="sectionSubjectId"
                    control={control}
                    rules={{ required: "Subject is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={subjectsData.map((subject) => ({
                          value: subject.id,
                          label: subject.name,
                        }))}
                        styles={customSelectStyles}
                        classNamePrefix="select"
                        placeholder="Select subject"
                        isClearable
                      />
                    )}
                  />
                  {errors.sectionSubjectId && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.sectionSubjectId.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div>
                {/* Diary Date Input */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-calendar-alt" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Diary Date <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    {...register("dairyDate", {
                      required: "Diary Date is required",
                      validate: (value) => 
                        value >= new Date().toISOString().split('T')[0] || 
                        "Past dates are not allowed",
                    })}
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#111827",
                      outline: "none",
                      transition: "border-color 0.2s"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                    onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                  />
                  {errors.dairyDate && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.dairyDate.message}
                    </p>
                  )}
                </div>

                {/* Title Input */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-heading" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Title <span style={{ color: "#ef4444" }}>*</span>
                    <span style={{
                      float: "right",
                      fontSize: "11px",
                      color: "#9ca3af",
                      fontWeight: "normal"
                    }}>
                      {subjectValue?.length || 0}/75 characters
                    </span>
                  </label>
                  <input
                    {...register("subject", {
                      required: "Title is required",
                      maxLength: {
                        value: 75,
                        message: "Maximum 75 characters allowed",
                      },
                    })}
                    type="text"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#111827",
                      outline: "none",
                      transition: "border-color 0.2s"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                    onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                    placeholder="Enter diary title"
                  />
                  {errors.subject && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                {/* Description Textarea */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-align-left" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Description
                    <span style={{
                      float: "right",
                      fontSize: "11px",
                      color: "#9ca3af",
                      fontWeight: "normal"
                    }}>
                      {descriptionValue?.length || 0}/250 characters
                    </span>
                  </label>
                  <textarea
                    {...register("description", {
                      maxLength: {
                        value: 250,
                        message: "Maximum 250 characters allowed",
                      },
                    })}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#111827",
                      outline: "none",
                      transition: "border-color 0.2s",
                      resize: "vertical",
                      minHeight: "100px"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                    onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                    placeholder="Enter diary description..."
                    rows="4"
                  />
                  {errors.description && (
                    <p style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.description.message}
                    </p>
                  )}
                </div>

                {/* Attachments Section */}
                <div style={{ marginBottom: "20px" }}>
                  <label style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#374151"
                  }}>
                    <i className="fas fa-paperclip" style={{ marginRight: "6px", color: "#6b7280" }}></i>
                    Attachments
                  </label>
                  <div style={{
                    border: "2px dashed #d1d5db",
                    borderRadius: "6px",
                    padding: "16px",
                    textAlign: "center",
                    backgroundColor: "#f9fafb"
                  }}>
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="file-upload"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                      }}
                    >
                      <div style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        backgroundColor: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "12px"
                      }}>
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: "20px", color: "#6b7280" }}></i>
                      </div>
                      <p style={{ margin: "0 0 4px 0", fontSize: "14px", color: "#374151", fontWeight: 500 }}>
                        Click to upload files
                      </p>
                      <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
                        Supported: Images, PDF, Documents (Max 10MB each)
                      </p>
                    </label>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div style={{ marginTop: "16px" }}>
                        <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#6b7280", fontWeight: 500 }}>
                          Selected Files ({selectedFiles.length})
                        </p>
                        <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "8px 12px",
                                backgroundColor: "#ffffff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "4px",
                                marginBottom: "4px"
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <i className={file.isExisting ? "fas fa-link" : "fas fa-file"} style={{ color: "#3b82f6" }}></i>
                                <div>
                                  <p style={{ margin: 0, fontSize: "12px", color: "#111827" }}>
                                    {file.isExisting ? (
                                      <a 
                                        href={`https://api.inspirertechnologies.com/ExternalImages/${file.path}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: "#3b82f6", textDecoration: "none" }}
                                      >
                                        {file.name.length > 30 ? file.name.substring(0, 30) + "..." : file.name}
                                        <i className="fas fa-external-link-alt" style={{ marginLeft: "5px", fontSize: "10px" }}></i>
                                      </a>
                                    ) : (
                                      file.name.length > 30 ? file.name.substring(0, 30) + "..." : file.name
                                    )}
                                  </p>
                                  <p style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#9ca3af" }}>
                                    {file.isExisting ? "Cloud Storage" : `${(file.size / 1024).toFixed(1)} KB`}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#ef4444",
                                  cursor: "pointer",
                                  fontSize: "14px"
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{
              paddingTop: "20px",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px"
            }}>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditId(null);
                  reset();
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#e5e7eb"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "#f3f4f6"}
              >
                <i className="fas fa-redo"></i>
                {isEditing ? "Cancel Edit" : "Reset Form"}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: "10px 24px",
                  backgroundColor: isEditing ? "#10b981" : "#3b82f6",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = isEditing ? "#059669" : "#2563eb";
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = isEditing ? "#10b981" : "#3b82f6";
                }}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {isEditing ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <i className={isEditing ? "fas fa-check-circle" : "fas fa-save"}></i>
                    {isEditing ? "Update Diary Entry" : "Save Diary Entry"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Records Tab - Report Style */
        <div style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          overflow: "hidden"
        }}>
          {/* Records Header */}
          <div style={{
            padding: "16px 20px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 600,
                color: "#111827",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <i className="fas fa-history" style={{ color: "#10b981" }}></i>
                Diary Records History
              </h2>
              <p style={{
                margin: "4px 0 0 0",
                fontSize: "13px",
                color: "#6b7280"
              }}>
                Showing {diariesData.length} diary entries
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Redundant date removed as per user request */}
            </div>
          </div>

          {/* Records Table */}
          <div style={{ padding: "20px" }}>
            {diariesData.length === 0 ? (
              <div style={{
                textAlign: "center",
                padding: "40px",
                border: "1px dashed #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#f9fafb"
              }}>
                <i className="fas fa-clipboard-list" style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }}></i>
                <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#374151" }}>
                  No Diary Records Found
                </h3>
                <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                  Create your first diary entry to get started
                </p>
              </div>
            ) : (
              <div style={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                overflow: "hidden"
              }}>
                <div style={{
                  padding: "12px 16px",
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <p style={{ margin: 0, fontSize: "14px", color: "#374151", fontWeight: 500 }}>
                      All Diary Entries
                    </p>
                    <div style={{
                      backgroundColor: "#e5e7eb",
                      color: "#4b5563",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: 500
                    }}>
                      {diariesData.length} records
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                    Last updated: {new Date().toLocaleTimeString()}
                  </p>
                </div>
                
                <CustomDatatableGrid
                  columns={columns}
                  data={diariesData}
                  enablePagination={true}
                  enableSorting={true}
                  enableColumnFilters={true}
                  muiTablePaperProps={{
                    sx: {
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      margin: 0
                    },
                  }}
                  muiTableContainerProps={{
                    sx: {
                      border: "none",
                      borderRadius: 0,
                      overflow: "hidden",
                    },
                  }}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#f9fafb",
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: "13px",
                      borderBottom: "1px solid #e5e7eb",
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      fontSize: "13px",
                      borderBottom: "1px solid #f3f4f6",
                    },
                  }}
                  muiTableBodyRowProps={{
                    sx: {
                      "&:hover": {
                        backgroundColor: "#f9fafb",
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>

          {/* Records Footer */}
          <div style={{
            padding: "12px 20px",
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Report generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => window.print()}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}
              >
                <i className="fas fa-print"></i>
                Print
              </button>
              <button
                onClick={() => setActiveTab("form")}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}
              >
                <i className="fas fa-plus"></i>
                New Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiarySetup;